/**
 * 3D Room Visualizer — Type Definitions
 */

import type { Yard } from '../../../calculations/plan/yard';
import type * as React from 'react';
import * as THREE from 'three';
import type { StagedPropView } from './stagingMeshes';
import type { CeilingPlane } from '../../../calculations/ceiling/profile';
import type { SlatWallAccessoryKind } from '../../../data/slatwallAccessories';

// ============================================================================
// BOARD LAYOUT TYPES
// ============================================================================

/** A single board/plank rectangle positioned on a wall surface */
export interface BoardRect {
  /** Horizontal offset from wall left edge (feet) */
  x: number;
  /** Vertical offset from wall bottom edge (feet) */
  y: number;
  /** Board dimension along wall's horizontal axis (feet) */
  width: number;
  /** Board dimension along wall's vertical axis (feet) */
  height: number;
  /** Hex color string: '#FFFFFF' or '#D0D3D4' */
  color: string;
  /** Product type */
  productType: 'WCB' | 'SlatWall';
  /** True if this board was trimmed to fit */
  isCut: boolean;
  /** Which edge was cut, if any */
  cutEdge?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Which way the board runs: true = up the wall (a strip), false = along it (a
   * row). When absent the mesh builder goes by shape (taller than wide = a
   * strip), which is right for whole boards but not for a short offcut or a
   * notched piece — so layouts that can produce those set it.
   */
  vertical?: boolean;
  /**
   * Set on the pieces of a board that is drawn as several rectangles (a board
   * notched around a window is one board, cut to shape): pieces with the same
   * boardId are the same physical board, so there is no joint between them.
   */
  boardId?: number;
  /**
   * W&CB, on a notched board: where the board's COURSE starts across the boards
   * (its y for a row, its x for a strip). A piece cut narrower than the course
   * is drawn as the whole course's profile clipped to the piece — never a
   * squashed profile — so it lines up with the board's other pieces exactly.
   */
  courseStart?: number;
  /** W&CB, with courseStart: how deep the board's course is across the boards (16", or less for a ripped last course). */
  courseSize?: number;
  /**
   * SlatWall only: this rectangle is a strip CUT from a row (beside a window or
   * over a door), and `rowY` is where that row starts. The row is drawn in its
   * usual place and everything outside the rectangle's height is cut away.
   */
  rowY?: number;
}

/**
 * One piece of edge trim, as the rectangle its visible face covers on the
 * surface (feet, origin bottom-left — the same coordinates as boards).
 */
export interface TrimPiece {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Hex color string: '#FFFFFF' or '#D0D3D4' */
  color: string;
  /** Trim product acronym ('JSnap', 'H', …). */
  kind: string;
  /** The product this trim finishes — sets how far it stands off the wall. */
  productType: 'WCB' | 'SlatWall';
  /**
   * A trim along the sloped top of a wall (under a gable or shed ceiling): the
   * wall's height at the piece's left and right ends. `x`/`width` span it,
   * `height` is the trim's face width, and `y` is unused.
   */
  rake?: [number, number];
}

/** A window or door on a wall, as the rectangle it cuts (surface coordinates, feet). */
export interface OpeningShape {
  x: number;
  y: number;
  width: number;
  height: number;
  kind: 'window' | 'door' | 'garageDoor' | 'passage';
  /** Which of its kind's styles to draw it in (openingStyles.ts); absent = the default. */
  style?: string;
  /**
   * Identifies the opening for opening / closing it in the 3D view: the same
   * for both copies of a door on a shared wall ('link:…'), so they move as one.
   */
  key?: string;
  /** Doors: hinge side (seen from this room) and which way it opens. */
  hinge?: 'left' | 'right';
  swing?: 'in' | 'out';
  /** A passage only: the wall thickness to finish through, and the panel colour to finish it in. */
  through?: number;
  color?: string;
}

/** A section of a wall containing boards of one product type */
export interface WallSectionLayout {
  sectionType: 'BelowSlatWall' | 'SlatWall' | 'AboveSlatWall' | 'FullWall';
  /** Vertical start position from floor (feet) */
  yStart: number;
  /** Section height (feet) */
  height: number;
  /** All boards in this section */
  boards: BoardRect[];
  /**
   * Id of the canonical Section that produced these boards (3D Designer). One
   * section may yield several layouts (a carved W&CB picture-frame), all sharing
   * this id, so the meshes can be tagged for reliable click-to-select. Optional —
   * wizard-driven layouts omit it.
   */
  sectionId?: string;
  /** Product type of the originating section (mirrors the boards'). */
  productType?: 'WCB' | 'SlatWall';
}

/** Complete board layout for one wall */
export interface WallBoardLayout {
  wallId: string;
  wallLength: number;
  wallHeight: number;
  sections: WallSectionLayout[];
  /** Edge trim (3D Designer). Wizard-driven layouts omit it. */
  trims?: TrimPiece[];
  /** Windows and doors (3D Designer). Wizard-driven layouts omit it. */
  openings?: OpeningShape[];
  /**
   * The wall's real shape when its top is not level (under a sloped ceiling):
   * counter-clockwise from the bottom-left in surface coordinates. The boards
   * are laid over the rectangle and cut to this. Absent = the rectangle.
   */
  outline?: [number, number][];
}

/**
 * One plane of a ceiling with a profile (shed, gable…). Its boards and trim
 * are laid in the plane's own surface coordinates — x along the ridge, y up
 * the slope, at the plane's true size — by the wall board builder, and put in
 * place by the plane's matrix.
 */
export interface CeilingPlaneLayout {
  id: CeilingPlaneFace;
  layout: WallBoardLayout;
  plane: CeilingPlane;
}

/** Complete board layout for a ceiling */
export interface CeilingBoardLayout {
  length: number;
  width: number;
  boards: BoardRect[];
  /** Id of the canonical Section behind the ceiling boards (3D Designer click-to-select). */
  sectionId?: string;
  /** Edge trim (3D Designer). Wizard-driven layouts omit it. */
  trims?: TrimPiece[];
}

/** All board layouts for a full room */
export interface RoomBoardLayouts {
  /** [front, back, left, right] */
  walls: WallBoardLayout[];
  /** A flat ceiling. */
  ceiling?: CeilingBoardLayout;
  /** A profiled ceiling: one layout per plane (instead of `ceiling`). */
  ceilingPlanes?: CeilingPlaneLayout[];
}

/** The ceiling's height over a point of the floor, from the layouts: a plane's slope, or the flat ceiling / wall height. */
export function ceilingHeightAt(layouts: RoomBoardLayouts | undefined, x: number, z: number, height: number): number {
  const plane = layouts?.ceilingPlanes?.find((p) => x >= p.plane.x0 - 1e-6 && x <= p.plane.x1 + 1e-6 && z >= p.plane.z0 - 1e-6 && z <= p.plane.z1 + 1e-6);
  return plane ? plane.plane.heightAt(x, z) : height;
}

/**
 * The ceiling's unit normal over a point of the floor, pointing INTO the room
 * (down-ish): a plane's own normal under a profiled ceiling, straight down
 * under a flat one. A fixture on the ceiling is set to it.
 */
export function ceilingNormalAt(layouts: RoomBoardLayouts | undefined, x: number, z: number): [number, number, number] {
  const plane = layouts?.ceilingPlanes?.find((p) => x >= p.plane.x0 - 1e-6 && x <= p.plane.x1 + 1e-6 && z >= p.plane.z0 - 1e-6 && z <= p.plane.z1 + 1e-6);
  return plane ? [plane.plane.normal[0], plane.plane.normal[1], plane.plane.normal[2]] : [0, -1, 0];
}

/** The highest point of the ceiling, from the layouts. */
export function ceilingPeak(layouts: RoomBoardLayouts | undefined, height: number): number {
  // A plane is flat along its ridge, so its highest point is at a corner.
  return Math.max(height, ...(layouts?.ceilingPlanes ?? []).flatMap((p) => [
    p.plane.heightAt(p.plane.x0, p.plane.z0), p.plane.heightAt(p.plane.x1, p.plane.z0),
    p.plane.heightAt(p.plane.x0, p.plane.z1), p.plane.heightAt(p.plane.x1, p.plane.z1),
  ]));
}

// ============================================================================
// 3D RENDERING TYPES
// ============================================================================

export type WallFace = 'front' | 'back' | 'left' | 'right';

/** A plane of a profiled ceiling, as a face: named by its plane id ('ceiling-start', 'ceiling-end', 'ceiling-slope'). */
export type CeilingPlaneFace = `ceiling-${string}`;

/** Any surface of the room that can be looked at head-on and edited. */
export type SurfaceFace = WallFace | 'ceiling' | CeilingPlaneFace;

/** The flat ceiling, or one of a profiled ceiling's planes. */
export function isCeilingFace(face: string | null | undefined): face is 'ceiling' | CeilingPlaneFace {
  return face === 'ceiling' || (typeof face === 'string' && face.startsWith('ceiling-'));
}

export interface WallTransparencyState {
  front: number;
  back: number;
  left: number;
  right: number;
  ceiling: number;
}

/** Refs to wall groups for dynamic opacity updates */
export interface WallGroupRefs {
  front?: THREE.Group;
  back?: THREE.Group;
  left?: THREE.Group;
  right?: THREE.Group;
  ceiling?: THREE.Group;
  floor?: THREE.Mesh;
}

/** Surface material refs for opacity animation */
export interface SurfaceMaterialRefs {
  front: THREE.MeshStandardMaterial[];
  back: THREE.MeshStandardMaterial[];
  left: THREE.MeshStandardMaterial[];
  right: THREE.MeshStandardMaterial[];
  ceiling: THREE.MeshStandardMaterial[];
}

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface GarageConfigProp {
  enabled: boolean;
  width: number;
  height: number;
  numDoors: number;
  /** Which wall the door is on (FullRoom only). Defaults to front wall. */
  onWallType?: 'LengthWall' | 'WidthWall';
}

export interface Room3DViewerProps {
  length: number;
  width: number;
  height?: number;
  showHeight?: boolean;
  highlightWalls?: boolean;
  highlightCeiling?: boolean;
  highlightFloor?: boolean;
  wallColor?: 'White' | 'Gray';
  ceilingColor?: 'White' | 'Gray';
  compact?: boolean;
  /** Enable board-level rendering */
  showBoards?: boolean;
  /** Panel orientation for W&CB */
  wallOrientation?: 'Horizontal' | 'Vertical';
  /** SlatWall configuration */
  slatWallConfig?: {
    enabled: boolean;
    startHeight: number;
    numPanelRows: number;
    color: 'White' | 'Gray';
  } | null;
  /** Whether to include ceiling boards */
  showCeiling?: boolean;
  /** Garage door configuration — placed on right wall in full room */
  garageConfig?: GarageConfigProp | null;
  /** Show procedural 2x4 stud framing outside the room walls. Default true. */
  showFraming?: boolean;
  /**
   * Pre-built board layouts. When provided, the viewer renders these instead of
   * generating layouts from the band config — used by the 3D Designer to render
   * the canonical section model (via sectionLayoutAdapter). Optional; existing
   * callers (wizard previews) omit it and are unaffected.
   */
  prebuiltLayouts?: RoomBoardLayouts;
  /**
   * Fill the parent container's height (height:100%) instead of the fixed-height
   * card — used by the full-screen 3D Designer. Optional; wizard previews omit it.
   */
  fillParent?: boolean;
  /**
   * Enable edit-mode interactions (3D Designer): click a wall/ceiling to rotate
   * head-on and lock; an "Exit Edit Mode" button unlocks. Off by default, so
   * wizard previews stay non-interactive beyond orbit/zoom.
   */
  editable?: boolean;
  /** Notifies the parent when a surface is locked (selected) or unlocked (null) in edit mode. */
  onFaceLock?: (face: SurfaceFace | null) => void;
  /** Reports a click on the locked surface in wall-local 2D coords (feet) so the parent can pick a section. */
  onSurfacePick?: (face: SurfaceFace, x: number, y: number) => void;
  /**
   * Reports the sectionId of the mesh clicked on the locked surface. Preferred
   * over onSurfacePick: it raycasts the tagged section meshes directly, so it is
   * robust to the extruded geometry's depth, T&G overlap, and groove recesses.
   * `null` means the click landed on bare wall — the host should clear its selection.
   */
  onSectionPick?: (sectionId: string | null) => void;
  /**
   * Reports the affine mapping from wall-local feet to CSS pixels (relative to
   * the viewer's container) for the currently-locked wall. Emitted when lock
   * settles, on resize, and cleared (null) on unlock / during a lock transition.
   * Walls and the ceiling alike. Used by the 3D Designer to render an HTML/SVG
   * handle overlay aligned to the locked head-on surface.
   */
  onProjectionChange?: (proj: SurfaceProjection | null) => void;
  /**
   * Reports whether the viewer's framing is currently visible. The Hide Framing
   * button is internal to the viewer, but the 3D Designer needs to know so it
   * can show a wall-perimeter outline overlay when framing is hidden (the studs
   * normally indicate the wall's edges).
   */
  onFramingToggle?: (visible: boolean) => void;
  /**
   * React content rendered INSIDE the 3D canvas container (same coordinate
   * system as the canvas — both share container's top-left as 0,0). Used by the
   * 3D Designer to mount the C4 handle overlay aligned to the projection.
   */
  overlay?: React.ReactNode;
  /**
   * Edit mode only: lock this surface as soon as the scene is ready — lets a host
   * restore the wall that was being edited (e.g. after a reload). Read once per
   * mount. If the surface doesn't exist, the viewer reports `onFaceLock(null)`.
   */
  initialLockedFace?: SurfaceFace | null;
  /**
   * Edit mode only: height in px at the top of the view to keep clear of the
   * locked surface — room for the host's floating controls, so they never cover
   * the surface's top edge or its handles. Default 0.
   */
  editInsetTop?: number;
  /** As `editInsetTop`, for the bottom of the view. Default 0. */
  editInsetBottom?: number;
  /** Imperative handle for hosts that drive the lock themselves (3D Designer). */
  apiRef?: React.MutableRefObject<Room3DViewerApi | null>;
  /**
   * The viewer is mounted but not on screen (the designer's floor planner is
   * open over it): keep the renderer, its context, programs and textures, but
   * build and draw nothing until it is shown again. Flipping this back on
   * builds the scene once, for whatever the props are by then.
   */
  suspended?: boolean;
  /**
   * Draw through the render pipeline (renderPipeline.ts): lights, shadows,
   * materials and lens effects all come from the live render settings, with
   * ambient occlusion, bloom and a tone curve on top. Off by default — the
   * wizard's viewers draw straight to the screen as before. Read once per mount.
   */
  enhancedRendering?: boolean;
  /**
   * Floor plans: the plan's other rooms, drawn around this one (the FOCUS room)
   * at their offsets from it. Locking, picking sections and editing stay with
   * the focus room; a click on another room asks to focus it.
   */
  contextRooms?: ContextRoom[];
  /** Staged props in the room shown (room3d/stagingMeshes.ts). */
  props?: StagedPropView[];
  /** The yard round the house (calculations/plan/yard.ts), sheet coordinates: built in the focus room's frame for the EXPORT only, never drawn in the view. */
  yard?: Yard;
  /**
   * Openings of the room shown the staging holds open — a garage door with a
   * car standing part way out through it — by opener key (`front:<id>`,
   * `link:<link>`). Opened when the scene is built and closed again once
   * nothing holds them; a door the user opened or closed by hand is left as
   * they left it.
   */
  heldOpen?: string[];
  /** The SlatWall accessories the user hung in the room shown (drawn in edit mode too). */
  accessories?: AccessoryView[];
  /** Stretches of the focus room's own framing that a neighbouring room's wall stands in for. */
  framingOmit?: Partial<Record<WallFace, FramingOmit[]>>;
  /** Identifies the focus room. When it changes, the camera travels to the new room. */
  roomKey?: string;
  /** Where the focus room's front-left corner is on the plan (feet) — for that journey. */
  roomOrigin?: { x: number; z: number };
  /** A click on another room (its floor or a wall). */
  onRoomFocus?: (roomId: string) => void;
  /** A double-click on any room's floor, from the orbit view. */
  onFloorDoubleClick?: () => void;
}

/**
 * Part of a wall's framing that is not built, in the wall's SURFACE x (feet):
 * a neighbouring room's wall stands there instead. With `below`, only up to
 * that height is left out — this room is the taller one and frames the rest.
 */
export interface FramingOmit {
  from: number;
  to: number;
  below?: number;
}

/** Another room of the plan, placed relative to the focus room. */
export interface ContextRoom {
  id: string;
  /** Its front-left inside corner, measured from the focus room's (feet; x along length, z along width). */
  offsetX: number;
  offsetZ: number;
  length: number;
  width: number;
  height: number;
  layouts: RoomBoardLayouts;
  framingOmit?: Partial<Record<WallFace, FramingOmit[]>>;
  /** Staged props in this room (room3d/stagingMeshes.ts). */
  props?: StagedPropView[];
  /** The SlatWall accessories the user hung in this room. */
  accessories?: AccessoryView[];
}

/** A SlatWall accessory the user hung, as the viewer draws it: its kind and its bottom-left on its wall (surface feet). */
export interface AccessoryView {
  id: string;
  kind: SlatWallAccessoryKind;
  face: WallFace;
  x: number;
  y: number;
}

export interface Room3DViewerApi {
  lockToFace: (face: SurfaceFace) => void;
  exitEditMode: () => void;
  /**
   * The room as built, for rendering elsewhere: a .glb of everything in it and
   * a manifest of the camera, lamps and look (room3d/sceneExport.ts). Null
   * before the scene exists.
   */
  /**
   * `panoramaFace`: the wall the 360 opens on (panorama.ts); the front wall
   * when not said. `camera`: the manifest's camera when the user is not inside
   * the room (else the user's own view is always used).
   */
  exportScene: (options?: { panoramaFace?: WallFace; camera?: import('./renderPose').CameraPose }) => Promise<{ glb: ArrayBuffer; manifest: import('./sceneExport').RenderManifest } | null>;
  /**
   * The camera a picture of the room is taken with: the user's own view when
   * they are inside the room, else a corner view that shows `feature` (and
   * `secondary`, on the other axis) — renderPose.ts. Null before the scene exists.
   */
  framing: (feature: WallFace, secondary?: WallFace) => import('./renderPose').CameraPose | null;
  /**
   * A still of the room as the viewer draws it, at `width` × `height`, from
   * `pose` (or the current view), as a JPEG data URL with the camera it was
   * taken with. The view is put back as it was. Null while the viewer is
   * suspended, has no scene, or has lost its context.
   */
  snapshot: (options: { width: number; height: number; pose?: import('./renderPose').CameraPose; quality?: number }) => Promise<{ dataUrl: string; camera: import('./sceneExport').RenderManifest['camera'] } | null>;
}

/**
 * Wall-local feet <-> container CSS pixels affine map for the locked head-on wall.
 * `screenPx = originPx + sx * axisX + sy * axisY` where (sx, sy) is in feet, with
 * origin at the wall's bottom-left and +Y upward. For an axis-aligned head-on
 * projection axisX = (kx, 0) and axisY = (0, -ky).
 */
export interface SurfaceProjection {
  face: SurfaceFace;
  originPx: { x: number; y: number };
  axisX: { x: number; y: number };
  axisY: { x: number; y: number };
}
